import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { CapabilityDialog } from "@/components/authoring/capability-dialog";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { deleteCapability } from "@/lib/api/endpoints";
import { useCapabilities } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { CapabilityView } from "@/types/api";

const RISK_VARIANT: Record<string, "secondary" | "warning" | "destructive"> = {
  read: "secondary",
  sensitive_read: "warning",
  write: "warning",
  external: "destructive",
};

export default function CapabilitiesPage() {
  const { data, isLoading, isError, error, refetch } = useCapabilities();
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CapabilityView | undefined>();
  const [toDelete, setToDelete] = React.useState<CapabilityView | undefined>();

  const del = useMutation({
    mutationFn: (toolName: string) => deleteCapability(toolName),
    onSuccess: async () => {
      toast.success("Tool deleted.");
      await qc.invalidateQueries({ queryKey: qk.capabilities });
      setToDelete(undefined);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div>
      <PageHeader
        title="Tools"
        description="Capabilities your agents can call — MCP servers, HTTP endpoints, and more."
        actions={
          <Button
            onClick={() => {
              setEditing(undefined);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            New tool
          </Button>
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          title="No tools yet"
          hint="Register an MCP server or HTTP tool your agents can call."
        />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Tool name</TH>
                <TH>Type</TH>
                <TH>Risk</TH>
                <TH>Domain</TH>
                <TH>Used by</TH>
                <TH className="w-24 text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {(data ?? []).map((c) => (
                <TR key={c.id}>
                  <TD className="font-medium">{c.name}</TD>
                  <TD className="font-mono text-xs text-muted-foreground">{c.tool_name}</TD>
                  <TD>
                    <Badge variant="outline" className="font-mono">
                      {c.execution_type}
                    </Badge>
                  </TD>
                  <TD>
                    <Badge variant={RISK_VARIANT[c.risk_tier] ?? "secondary"}>{c.risk_tier}</Badge>
                  </TD>
                  <TD className="text-muted-foreground">{c.domain_slug}</TD>
                  <TD>{c.attached_count}</TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit"
                        onClick={() => {
                          setEditing(c);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={() => setToDelete(c)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <CapabilityDialog open={dialogOpen} onOpenChange={setDialogOpen} capability={editing} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(undefined)}
        title={`Delete tool “${toDelete?.name}”?`}
        description="Tools attached to a version can't be deleted until detached."
        pending={del.isPending}
        onConfirm={() => toDelete && del.mutate(toDelete.tool_name)}
      />
    </div>
  );
}
